import React, { useCallback, useEffect, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight01Icon, Search01Icon } from '@hugeicons/core-free-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { Kbd } from '@/components/ui/kbd';
import { setCommandPaletteOpen } from '../../../redux/slices/commandPaletteSlice';
import Icon from '../Icon';
import { executeCommandAction } from './commandPaletteActions';
import useCommandItems from './useCommandItems';
import useCommandPalette from './useCommandPalette';

/**
 * The ⌘K palette on the shadcn Command (cmdk): search field, grouped single-line commands and
 * an empty state. Descriptions still feed the search ranking but aren't shown, to keep rows
 * scannable. cmdk handles arrow keys, Enter and hover selection; the items, their access
 * rules and the search ranking still come from useCommandItems, so cmdk's own filter is off.
 */
function CommandPalette() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen, close } = useCommandPalette();
  const [searchQuery, setSearchQuery] = useState('');

  const { groupedItems, isSearching } = useCommandItems(searchQuery);
  const isDarkTheme = useSelector(state => state.app.isDarkTheme);

  const handleClose = useCallback(() => {
    close();
    setSearchQuery('');
  }, [close]);

  const handleSelect = useCallback(
    item => {
      dispatch(setCommandPaletteOpen(false));
      setSearchQuery('');

      if (item.actionType) {
        executeCommandAction(item, { dispatch, isDarkTheme });
        return;
      }

      if (item.path) {
        navigate(item.path);
      }
    },
    [dispatch, isDarkTheme, navigate]
  );

  useEffect(() => {
    if (isOpen) setSearchQuery('');
  }, [isOpen]);

  const groups = Object.entries(groupedItems);
  const query = searchQuery.trim();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={next => {
        if (!next) handleClose();
      }}
      title="Command palette"
      description="Search pages, actions and settings."
      // Same corner as the portal's dialogs; rows are rounded-md like its menus, not pills.
      // Vertically centred at full height (~32rem); the top edge stays fixed while results
      // filter, so the search field never jumps as the list shrinks.
      className="top-[max(1rem,calc(50%-16rem))] gap-0 rounded-xl! sm:max-w-[560px]"
    >
      <Command shouldFilter={false} loop className="rounded-none p-1.5 pb-0">
        <CommandInput
          autoFocus
          placeholder="Search pages, actions, settings…"
          value={searchQuery}
          onValueChange={setSearchQuery}
          className="h-10 text-[14px]"
        />
        <CommandList className="max-h-[min(420px,60vh)] pt-1">
          <CommandEmpty className="flex flex-col items-center gap-2 px-6 py-10">
            <span className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <HugeiconsIcon icon={Search01Icon} strokeWidth={2} className="size-5" />
            </span>
            <span className="text-sm font-medium text-foreground">No results for “{query}”</span>
            <span className="text-[13px] text-muted-foreground">
              Try a page or action, like Agents, Leads or Create agent.
            </span>
          </CommandEmpty>
          {groups.map(([group, items], groupIndex) => (
            <React.Fragment key={group}>
              {groupIndex > 0 && <CommandSeparator />}
              <CommandGroup heading={group}>
                {items.map(item => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => handleSelect(item)}
                    className="gap-3 rounded-md! px-2 py-1.5 in-data-[slot=dialog-content]:rounded-md"
                  >
                    {/* Icon tile like the dashboard's: muted, brand-tinted when selected. */}
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors duration-150 ease-out-quad group-data-selected/command-item:bg-primary/10 group-data-selected/command-item:text-primary">
                      <Icon name={item.icon} style={{ width: 15, height: 15 }} />
                    </span>
                    <span className="min-w-0 truncate">
                      {isSearching ? (
                        <Highlighter
                          highlightClassName="bg-transparent p-0 font-semibold text-primary"
                          searchWords={[searchQuery.trim()]}
                          autoEscape
                          textToHighlight={item.label}
                        />
                      ) : (
                        item.label
                      )}
                    </span>
                    {/* Shows on the selected row only: what Enter does. */}
                    <CommandShortcut className="opacity-0 transition-opacity duration-150 ease-out-quad group-data-selected/command-item:opacity-100">
                      <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={2} className="size-4" />
                    </CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            </React.Fragment>
          ))}
        </CommandList>
      </Command>
      <div className="flex items-center gap-4 border-t bg-muted/30 px-4 py-2.5 text-xs text-muted-foreground select-none">
        <span className="flex items-center gap-1.5">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          Move
        </span>
        <span className="flex items-center gap-1.5">
          <Kbd>↵</Kbd>
          Open
        </span>
        <span className="flex items-center gap-1.5">
          <Kbd>esc</Kbd>
          Close
        </span>
        <span className="ms-auto flex items-center gap-1.5">
          <Kbd>{isMac ? '⌘' : 'Ctrl'} K</Kbd>
          Anywhere
        </span>
      </div>
    </CommandDialog>
  );
}

export default CommandPalette;
